import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { KundliStudio, SavedKundlis, useSavedKundlis } from '@astro/kundli-studio'
import { normaliseDob, normaliseTime } from '../useDashboard'

/* ===================== KUNDLI (seeker) =====================
   Two sub-tabs, the active one in the URL (?tab=kundli&view=saved) so a
   reload or the back button lands on the same screen:

   - Kundli Studio — the signed-in user's complete Vedic analysis, cast
     automatically from the saved profile birth details (no re-typing what we
     already know). Accounts without saved details get the studio's own form,
     and "Cast for Someone Else" covers on-demand charts for family & friends.
     Missing dob/time/place (registration doesn't ask for them) means the cast
     runs on stand-ins — 12:00, the city — so a notice links to My Profile.
   - Saved Kundlis — every chart this account has cast, filtered server-side
     (search, gender, source, birth-date range, sort). Opening one switches
     back to the studio and re-casts it there.

   The studio stays mounted while hidden, so flipping sub-tabs never loses
   the open chart or repeats its VedAstro calls. */

const VIEWS = [
  { key: 'studio', label: 'Kundli Studio' },
  { key: 'saved', label: 'Saved Kundlis' },
]

export default function KundliTab({ data, onSwitch } = {}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const view = searchParams.get('view') === 'saved' ? 'saved' : 'studio'
  const setView = (key) => {
    const next = new URLSearchParams(searchParams)
    if (key === 'saved') next.set('view', 'saved')
    else next.delete('view') // the studio is the tab's canonical URL
    setSearchParams(next)
  }
  // A fresh object per pick, so reopening the same row twice still re-casts.
  const [reopen, setReopen] = useState(null)
  const openSaved = (row) => {
    setReopen({ ...row })
    setView('studio')
  }
  const { total: savedTotal } = useSavedKundlis()

  const profile = data?.profile
  const loading = Boolean(data?.profileLoading)
  // Profile dob/time arrive in whatever format the backend stored — normalise
  // to ISO/24h or the studio's auto-cast rejects them with a visible error.
  const dob = normaliseDob(profile?.dob)
  const saved = dob
    ? {
      name: profile.name || '',
      dob,
      tob: normaliseTime(profile.birthTime) || '12:00',
      pob: profile.birthPlace || '',
    }
    : null

  const missing = profile && !loading
    ? [
      !dob && 'date of birth',
      !profile.birthTime && !profile.birthTimeUnknown && 'birth time',
      !profile.hasBirthPlace && 'city (birth place)',
    ].filter(Boolean)
    : []

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h3 className="text-xl text-astroDark font-royal">{view === 'saved' ? 'Saved Kundlis' : 'Kundli Studio'}</h3>
          <p className="text-xs text-stone-500 mt-1">
            {view === 'saved'
              ? 'Every chart you cast is kept here — search and filter them, then open any one in the studio.'
              : saved || loading
                ? 'Cast automatically from your saved birth details — divisional charts, dashas, strengths, yogas & your full life report.'
                : 'Enter your birth details once to unlock divisional charts, dashas, strengths, yogas & your full life report.'}
          </p>
        </div>
      </div>

      {/* Sub-tabs — segmented pills, same idiom as Consultations. */}
      <div role="tablist" aria-label="Kundli sections" className="flex flex-wrap gap-1 rounded-xl p-1 w-max max-w-full shrink-0 border border-amber-900/20 bg-amber-50/60">
        {VIEWS.map((v) => {
          const active = v.key === view
          return (
            <button
              key={v.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setView(v.key)}
              className={`flex items-center gap-1.5 px-5 py-3 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${active ? 'bg-[#3D0A0A] text-white shadow-md shadow-[#3D0A0A]/20' : 'text-stone-500 hover:text-astroDark hover:bg-white cursor-pointer'
                }`}
            >
              {v.label}
              {v.key === 'saved' && savedTotal > 0 && (
                <span className={`num text-[9px] px-1.5 py-0.5 rounded-full ${active ? 'bg-[#D4A017] text-[#3D0A0A]' : 'bg-stone-200/80 text-stone-500'}`}>{savedTotal}</span>
              )}
            </button>
          )
        })}
      </div>

      {view === 'studio' && missing.length > 0 && (
        <div role="status" className="rounded-xl border border-amber-900/10 bg-amber-50/60 px-4 py-3 text-xs text-stone-600">
          <span className="font-semibold text-amber-800">To get accurate results, please update your profile details first.</span>{' '}
          Your {formatList(missing)} {missing.length > 1 ? 'are' : 'is'} missing.{' '}
          <a
            href="?tab=myprofile"
            onClick={(e) => {
              if (!onSwitch) return
              e.preventDefault()
              onSwitch('myprofile')
            }}
            className="font-semibold text-amber-700 underline underline-offset-2 hover:text-amber-900"
          >
            Update my profile
          </a>
        </div>
      )}
      <div hidden={view !== 'studio'}>
        <KundliStudio initialBirth={saved} loading={loading} reopen={reopen} savedTab />
      </div>
      {view === 'saved' && (
        <div className="kundli-studio">
          <SavedKundlis filterable onOpen={openSaved} />
        </div>
      )}
    </div>
  )
}

/** ['a','b','c'] → 'a, b and c'. */
function formatList(items) {
  return items.length > 1 ? `${items.slice(0, -1).join(', ')} and ${items.at(-1)}` : items[0]
}
